import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { Button, Flex, Radio } from 'antd';
import FadeIn from '../FadeIn';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useApiClient from '../../hooks/api/useApiClient';
import { PROMO_CODE_DISCOUNT_TYPES, PromoCodeTag } from '../../services/exports/Constants';
import PromoCodeConstructor from '../promo-code/PromoCodeConstructor';
import useHelpers from '../../hooks/context/useHelpers';
import { PROMO_CODES_TAGS, promoCodesApiSlice } from '../../store/api-slices';

export default function PromoCodeStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:PromoCodeStep' });

  const { isSkippable, moveBackwards, skip, skipping } = props;

  const dispatch = useDispatch();
  const { company } = useSelector((store) => store.currentCompany);

  const { PromoCodesManager } = useApiClient();
  const { formatCurrency } = useHelpers();

  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [options, setOptions] = useState([
    {
      franchise_id: company.franchise_id,
      tag: PromoCodeTag.Master,
      discount_type: PROMO_CODE_DISCOUNT_TYPES.fixed_amount,
      code: company.name
        .replace(/[^A-Za-z0-9]/, '')
        .slice(0, 8)
        .toUpperCase(),
      quantity: null,
      components: [
        { discount: 4, usage_threshold: 20 },
        { discount: 3, usage_threshold: 20 },
        { discount: 3, usage_threshold: 20 },
      ],
    },
    {
      franchise_id: company.franchise_id,
      tag: PromoCodeTag.Master,
      code: company.name
        .replace(/[^A-Za-z0-9]/, '')
        .slice(0, 8)
        .toUpperCase(),
      discount_type: PROMO_CODE_DISCOUNT_TYPES.fixed_amount,
      quantity: null,
      components: [
        { discount: 3, usage_threshold: 16 },
        { discount: 3, usage_threshold: 16 },
        { discount: 2, usage_threshold: 16 },
      ],
    },
    {
      franchise_id: company.franchise_id,
      tag: PromoCodeTag.Master,
      code: company.name
        .replace(/[^A-Za-z0-9]/, '')
        .slice(0, 8)
        .toUpperCase(),
      discount_type: PROMO_CODE_DISCOUNT_TYPES.fixed_amount,
      quantity: null,
      components: [
        { discount: 2, usage_threshold: 10 },
        { discount: 2, usage_threshold: 10 },
        { discount: 2, usage_threshold: 10 },
      ],
    },
  ]);

  const updateOption = (index, field, value) =>
    setOptions((current) =>
      collect(current)
        .put(index, {
          ...current[index],
          [field]: value,
        })
        .toArray(),
    );

  const [loading, setLoading] = useState(false);
  const { setFeedback, renderFeedbackLabel } = useFeedbackLabel();

  async function save() {
    if (selectedOptionIndex === null) {
      return skip();
    }

    setLoading(true);
    const { data: responseData, success } = await PromoCodesManager.create(options[selectedOptionIndex]);
    setLoading(false);

    if (!success) {
      return setFeedback({
        message: responseData?.message,
        type: 'error',
        errors: responseData?.errors,
      });
    }

    dispatch(promoCodesApiSlice.util.invalidateTags([PROMO_CODES_TAGS.PROMO_CODES]));

    return skip();
  }

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <div>
        {options.map((item, index) => (
          <>
            <Flex className="mb-md">
              <Radio
                id={`promo-code-option-${index}`}
                checked={index === selectedOptionIndex}
                onClick={() => setSelectedOptionIndex(index)}
              />
              <label htmlFor={`promo-code-option-${index}`} className="ml-xs my-auto cursor-pointer">
                {formatCurrency(collect(item.components).sum('discount'))}
              </label>
            </Flex>
            {index === selectedOptionIndex && (
              <PromoCodeConstructor
                discountType={item.discount_type}
                components={item.components}
                setComponents={(value) => updateOption(index, 'components', value)}
              />
            )}
          </>
        ))}
        <Flex className="mb-md">
          <Radio
            id="promo-code-option-none"
            checked={selectedOptionIndex === null}
            onClick={() => setSelectedOptionIndex(null)}
          />
          <label htmlFor="promo-code-option-none" className="ml-xs my-auto cursor-pointer">
            {t('options.none')}
          </label>
        </Flex>
      </div>
      <Flex justify="space-between" className="mt-md">
        {isSkippable ? (
          <Button color="default" variant="solid" onClick={skip} loading={skipping}>
            {t('buttons.skip')}
          </Button>
        ) : (
          <span />
        )}
        <Flex justify="end">
          {moveBackwards && (
            <Button onClick={moveBackwards} className="mr-md">
              {t('buttons.back')}
            </Button>
          )}
          <Button type="primary" onClick={save} loading={loading}>
            {t('buttons.next')}
          </Button>
        </Flex>
      </Flex>
    </FadeIn>
  );
}
